"use client";

import {
	Check,
	PlaySquareIcon,
	TrendingUp,
	Users,
	Video,
	ArrowUpRight,
	Zap,
	BarChart3,
	Globe,
	MessageCircle,
} from "lucide-react";
import Image from "next/image";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { staggerContainer, fadeInUp } from "@/lib/animations";

import { Button } from "@/components/ui/button";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { AnimatedText } from "@/components/custom/animated-text";
import { MagneticButton } from "@/components/custom/magnetic-button";
import { cn } from "@/lib/utils";

// JSON-LD Service Schema for SEO
const serviceJsonLd = {
	"@context": "https://schema.org",
	"@type": "Service",
	serviceType: "YouTube Channel Promotion",
	name: "TheHighraysYT - YouTube Growth Services",
	description:
		"Expert YouTube promotion services including channel growth, audience engagement, content optimization, and organic subscriber building.",
	provider: {
		"@type": "Organization",
		name: "TheHighrays",
		url: "https://thehighrays.com",
	},
	areaServed: {
		"@type": "Place",
		name: "Worldwide",
	},
	hasOfferCatalog: {
		"@type": "OfferCatalog",
		name: "YouTube Growth Services",
		itemListElement: [
			{
				"@type": "Offer",
				itemOffered: {
					"@type": "Service",
					name: "Channel Growth",
					description:
						"Strategic solutions to grow your subscriber base and increase your channel's visibility.",
				},
			},
			{
				"@type": "Offer",
				itemOffered: {
					"@type": "Service",
					name: "Audience Engagement",
					description:
						"Techniques to boost viewer interaction and build a loyal community around your content.",
				},
			},
			{
				"@type": "Offer",
				itemOffered: {
					"@type": "Service",
					name: "Content Optimization",
					description:
						"Expert advice on optimizing your videos for maximum reach and impact on YouTube's platform.",
				},
			},
		],
	},
};

const faqJsonLd = {
	"@context": "https://schema.org",
	"@type": "FAQPage",
	mainEntity: [
		{
			"@type": "Question",
			name: "How fast will I see results?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "Engagement typically spikes within the first 30 days. Real, sustainable subscriber growth follows shortly after as the algorithm picks up the new signals.",
			},
		},
		{
			"@type": "Question",
			name: "Is this organic growth?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "100%. We don't use bots or engagement farms. We use strategy to get your content in front of real humans who actually care about your niche.",
			},
		},
		{
			"@type": "Question",
			name: "Do you make the videos?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "We focus on strategy, packaging (thumbnails/titles), and promotion. You create the content, we make sure the world sees it.",
			},
		},
		{
			"@type": "Question",
			name: "Is my channel safe?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "Completely. We strictly adhere to YouTube's TOS. Our methods are white-hat and focused on long-term channel health.",
			},
		},
	],
};

const features = [
	{
		id: 1,
		title: "Channel Growth",
		icon: <TrendingUp className="h-8 w-8" />,
		description:
			"Strategic solutions to grow your subscriber base and increase your channel's visibility.",
	},
	{
		id: 2,
		title: "Audience Engagement",
		icon: <Users className="h-8 w-8" />,
		description:
			"Techniques to boost viewer interaction and build a loyal community around your content.",
	},
	{
		id: 3,
		title: "Content Optimization",
		icon: <Video className="h-8 w-8" />,
		description:
			"Expert advice on optimizing your videos for maximum reach and impact on YouTube's platform.",
	},
];

export default function TheHighraysYTPage() {
	const containerRef = useRef(null);
	const { scrollYProgress } = useScroll({
		target: containerRef,
		offset: ["start start", "end end"],
	});

	const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);

	return (
		<>
			{/* JSON-LD Structured Data */}
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{
					__html: JSON.stringify(serviceJsonLd),
				}}
			/>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{
					__html: JSON.stringify(faqJsonLd),
				}}
			/>
			<div ref={containerRef} className="flex min-h-screen flex-col bg-zinc-950 font-sans text-white selection:bg-red-500 selection:text-white">
			{/* Hero Section */}
			<section className="relative min-h-screen w-full overflow-hidden flex flex-col justify-center pt-20">
				<div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-30 mix-blend-overlay pointer-events-none"></div>
				<div className="absolute inset-0 bg-linear-to-b from-zinc-900/0 via-zinc-950/50 to-zinc-950 pointer-events-none"></div>

				{/* Abstract Background Elements */}
				<div className="absolute top-0 right-0 w-[50vw] h-[50vw] bg-red-600/20 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/4 pointer-events-none"></div>
				<div className="absolute bottom-0 left-0 w-[40vw] h-[40vw] bg-orange-600/10 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4 pointer-events-none"></div>

				<div className="container relative z-10 mx-auto px-4 md:px-10 h-full flex flex-col justify-center">
					<div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center h-full">
						{/* Text Content */}
						<motion.div
							variants={staggerContainer}
							initial="hidden"
							animate="show"
							className="lg:col-span-7 flex flex-col justify-center relative z-20"
						>
							<motion.div variants={fadeInUp} className="mb-6">
								<div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-red-500/30 bg-red-500/10 text-red-500 backdrop-blur-md">
									<span className="relative flex h-2 w-2">
										<span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
										<span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
									</span>
									<span className="font-mono text-xs font-bold uppercase tracking-widest">
										Viral Engineering
									</span>
								</div>
							</motion.div>

							<h1 className="font-syne text-[12vw] lg:text-[7vw] font-bold uppercase leading-[0.85] tracking-tighter text-white mix-blend-difference">
								<span className="block">Dominate</span>
								<span className="block text-transparent bg-clip-text bg-linear-to-r from-red-500 via-orange-500 to-yellow-500">
									The Algo
								</span>
								<span className="block">Rithm</span>
							</h1>

							<motion.p
								variants={fadeInUp}
								className="mt-8 max-w-xl font-inter text-xl text-zinc-400 leading-relaxed"
							>
								We don't just edit videos. We engineer viral moments.
								Expert YouTube promotion services to help creators explode their
								audience and monetize effectively.
							</motion.p>

							<motion.div variants={fadeInUp} className="mt-10 flex flex-wrap gap-6">
								<a
									href="https://www.thehighraysyt.com"
									target="_blank"
									rel="noopener noreferrer"
								>
									<MagneticButton className="h-16 px-8 bg-white text-black rounded-full font-syne text-lg font-bold uppercase tracking-wide hover:bg-zinc-200 transition-colors flex items-center gap-2">
										Start Growing
										<ArrowUpRight className="h-5 w-5" />
									</MagneticButton>
								</a>
								<a href="#services">
									<Button
										variant="ghost"
										className="h-16 px-8 rounded-full border border-white/20 text-white font-syne text-lg font-bold uppercase tracking-wide hover:bg-white hover:text-black hover:border-white transition-all duration-300"
									>
										Explore Services
									</Button>
								</a>
							</motion.div>
						</motion.div>

						{/* Hero Visual - Floating Cards */}
						<div className="lg:col-span-5 relative h-[65vh] lg:h-[70vh] w-full flex items-center justify-center">
							<div className="relative w-full h-full">
								{/* Abstract Background for Cards */}
								<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-red-500/10 rounded-full blur-[80px]"></div>

								{/* Floating Metric Cards */}

								{/* 1. Watch Time (Prominent - Center/Top) - Anchor */}
								<motion.div
									initial={{ opacity: 0, y: -30 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.8, delay: 0.2 }}
									className="absolute top-[20%] left-[50%] lg:top-[28%] lg:left-[40%] -translate-x-1/2 z-40"
								>
									<motion.div
										animate={{ y: [0, -12, 0] }}
										transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut" }}
										className="bg-zinc-900/80 backdrop-blur-xl border border-white/10 p-6 rounded-2xl shadow-2xl w-64 -rotate-3"
									>
										<div className="flex items-center gap-3 mb-2">
											<div className="h-2 w-2 rounded-full bg-yellow-500 animate-pulse"></div>
											<span className="font-mono text-xs text-zinc-400 uppercase tracking-widest">WatchTime</span>
										</div>
										<div className="font-sans text-5xl font-bold text-white tracking-tight">4,000h+</div>
										<div className="mt-2 text-[10px] text-zinc-400 font-medium bg-white/5 py-0.5 px-2 rounded-full inline-block">
											Monetization Ready
										</div>
									</motion.div>
								</motion.div>

								{/* 2. Viral Status (Top Right - Closer) */}
								<motion.div
									initial={{ opacity: 0, x: 30, y: -20 }}
									animate={{ opacity: 1, x: 0, y: 0 }}
									transition={{ duration: 0.8, delay: 0.4 }}
									className="absolute top-[15%] right-[5%] lg:top-[25%] lg:right-[5%] z-30"
								>
									<motion.div
										animate={{ y: [0, 15, 0], rotate: [0, 2, 0] }}
										transition={{ duration: 7.2, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
										className="bg-white text-black p-5 rounded-2xl shadow-xl w-40 rotate-3"
									>
										<Zap className="h-8 w-8 text-red-600 mb-2" />
										<div className="font-syne text-xl font-bold uppercase leading-none">
											Viral<br />Status
										</div>
									</motion.div>
								</motion.div>

								{/* 3. Total Views (Mid Left - Closer) */}
								<motion.div
									initial={{ opacity: 0, x: -30 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.8, delay: 0.5 }}
									className="absolute top-[50%] left-[5%] lg:top-[48%] lg:left-[8%] z-30"
								>
									<motion.div
										animate={{ y: [0, 20, 0], rotate: [0, -1, 0] }}
										transition={{ duration: 8.5, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
										className="bg-zinc-800/80 backdrop-blur-xl border border-white/10 p-5 rounded-2xl shadow-2xl w-56 -rotate-6"
									>
										<div className="flex items-center gap-3 mb-2">
											<div className="h-2 w-2 rounded-full bg-blue-500"></div>
											<span className="font-mono text-[10px] text-zinc-400 uppercase tracking-widest">Total Views</span>
										</div>
										<div className="font-sans text-4xl font-bold text-white tracking-tight">1M+</div>
										<div className="mt-3 h-1.5 w-full bg-zinc-700 rounded-full overflow-hidden">
											<motion.div
												initial={{ width: 0 }}
												animate={{ width: "85%" }}
												transition={{ duration: 1.5, delay: 0.8 }}
												className="h-full bg-blue-500 rounded-full"
											/>
										</div>
									</motion.div>
								</motion.div>

								{/* 4. Subscribers (Mid Right - Closer) */}
								<motion.div
									initial={{ opacity: 0, x: 40 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.8, delay: 0.6 }}
									className="absolute top-[60%] right-[5%] lg:top-[48%] lg:right-[8%] z-20"
								>
									<motion.div
										animate={{ y: [0, -15, 0], rotate: [0, 1, 0] }}
										transition={{ duration: 6.8, repeat: Infinity, ease: "easeInOut", delay: 2.1 }}
										className="bg-zinc-900/80 backdrop-blur-xl border border-white/10 p-5 rounded-2xl shadow-2xl w-48 rotate-1"
									>
										<div className="flex items-center gap-3 mb-2">
											<div className="h-2 w-2 rounded-full bg-red-500"></div>
											<span className="font-mono text-[10px] text-zinc-400 uppercase tracking-widest">Subscribers</span>
										</div>
										<div className="font-sans text-3xl font-bold text-white tracking-tight">100K+</div>
										<div className="mt-2 flex items-center gap-1 text-xs text-red-500 font-sans">
											<TrendingUp className="h-3 w-3" />
											<span>+12.5%</span>
										</div>
									</motion.div>
								</motion.div>

								{/* 5. Growth Rate (Bottom Left - Closer) */}
								<motion.div
									initial={{ opacity: 0, y: 30 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.8, delay: 0.7 }}
									className="absolute bottom-[8%] left-[5%] lg:bottom-[18%] lg:left-[18%] z-20"
								>
									<motion.div
										animate={{ y: [0, -18, 0], rotate: [0, 2, 0] }}
										transition={{ duration: 5.9, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
										className="bg-zinc-900/90 backdrop-blur-xl border border-white/10 p-6 rounded-2xl shadow-xl w-48 rotate-2"
									>
										<div className="flex items-center gap-3 mb-2">
											<div className="h-3 w-3 rounded-full bg-green-500"></div>
											<span className="font-mono text-xs text-zinc-400 uppercase">Growth Rate</span>
										</div>
										<div className="font-sans text-4xl font-bold text-white tracking-tight">+458%</div>
										<div className="mt-2 h-1 w-full bg-zinc-800 rounded-full overflow-hidden">
											<div className="h-full w-[80%] bg-green-500 rounded-full"></div>
										</div>
									</motion.div>
								</motion.div>

								{/* 6. Comments (Bottom Right - Closer) */}
								<motion.div
									initial={{ opacity: 0, x: 20, y: 20 }}
									animate={{ opacity: 1, x: 0, y: 0 }}
									transition={{ duration: 0.8, delay: 0.8 }}
									className="absolute bottom-[2%] right-[5%] lg:bottom-[22%] lg:right-[10%] z-30"
								>
									<motion.div
										animate={{ y: [0, 10, 0], rotate: [0, -2, 0] }}
										transition={{ duration: 6.2, repeat: Infinity, ease: "easeInOut", delay: 1.8 }}
										className="bg-zinc-900/90 backdrop-blur-xl border border-white/10 text-white p-5 rounded-2xl shadow-2xl w-48 -rotate-1"
									>
										<div className="flex items-center gap-3 mb-2">
											<MessageCircle className="h-4 w-4 text-white" />
											<span className="font-mono text-[10px] text-zinc-400 uppercase tracking-widest">Comments</span>
										</div>
										<div className="font-sans text-3xl font-bold text-white tracking-tight">10K+</div>
										<div className="mt-2 flex items-center gap-1 text-xs text-white font-sans">
											<TrendingUp className="h-3 w-3" />
											<span>+85%</span>
										</div>
									</motion.div>
								</motion.div>

								{/* Decorative Elements */}
								<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full border border-white/5 rounded-full scale-75 animate-[spin_20s_linear_infinite] opacity-20 pointer-events-none border-dashed"></div>
								<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] border border-white/5 rounded-full scale-90 animate-[spin_30s_linear_infinite_reverse] opacity-10 pointer-events-none"></div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Services Section */}
			<section id="services" className="relative py-32 overflow-hidden">
				<div className="container mx-auto px-4 md:px-10 relative z-10">
					<div className="mb-20">
						<h2 className="font-syne text-[10vw] lg:text-[8vw] font-bold uppercase leading-[0.8] tracking-tighter text-zinc-800 select-none absolute left-0 -top-10 -z-10 opacity-50">
							Services
						</h2>
						<div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
							<h2 className="font-syne text-5xl md:text-7xl font-bold uppercase tracking-tighter text-white">
								Our <span className="text-red-500">Arsenal</span>
							</h2>
							<p className="max-w-md font-inter text-zinc-400 text-lg">
								Everything you need to turn your channel into a powerhouse.
							</p>
						</div>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
						{features.map((feature, i) => (
							<motion.div
								key={feature.id}
								initial={{ opacity: 0, y: 50 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: i * 0.1 }}
								viewport={{ once: true }}
								className="group relative h-[400px] flex flex-col justify-between p-8 rounded-3xl border border-white/10 bg-zinc-900/50 backdrop-blur-sm overflow-hidden hover:border-red-500/50 transition-colors duration-500"
							>
								<div className="absolute inset-0 bg-linear-to-br from-red-500/0 via-red-500/0 to-red-500/0 group-hover:via-red-500/5 group-hover:to-red-500/10 transition-all duration-500"></div>

								<div className="relative z-10">
									<div className="w-16 h-16 rounded-2xl bg-zinc-800 flex items-center justify-center mb-6 group-hover:bg-red-500 group-hover:text-white transition-colors duration-300">
										{feature.icon}
									</div>
									<h3 className="font-syne text-3xl font-bold uppercase text-white mb-4">
										{feature.title}
									</h3>
									<p className="font-inter text-zinc-400 group-hover:text-zinc-300 transition-colors">
										{feature.description}
									</p>
								</div>

								<div className="relative z-10 flex justify-end">
									<div className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center group-hover:bg-white group-hover:text-black transition-all duration-300">
										<ArrowUpRight className="h-5 w-5" />
									</div>
								</div>
							</motion.div>
						))}
					</div>
				</div>
			</section>

			{/* How It Works - "The Blueprint" */}
			<section className="py-32 bg-zinc-900 relative">
				<div className="container mx-auto px-4 md:px-10">
					<div className="flex flex-col lg:flex-row gap-20">
						<div className="lg:w-1/3 sticky top-32 h-fit">
							<h2 className="font-syne text-6xl md:text-8xl font-bold uppercase tracking-tighter text-white mb-8">
								The <br /><span className="text-transparent bg-clip-text bg-linear-to-r from-red-500 to-orange-600">Blueprint</span>
							</h2>
							<p className="font-inter text-xl text-zinc-400">
								A systematic approach to engineered virality. No luck involved.
							</p>
						</div>

						<div className="lg:w-2/3 flex flex-col gap-0 border-l border-white/10">
							{[
								{
									step: "01",
									title: "Deep Audit",
									desc: "We tear down your channel to find the cracks in your strategy.",
									icon: <BarChart3 className="h-6 w-6" />
								},
								{
									step: "02",
									title: "Strategy Injection",
									desc: "We develop a custom roadmap tailored to your specific niche and goals.",
									icon: <Zap className="h-6 w-6" />
								},
								{
									step: "03",
									title: "Execution & Scale",
									desc: "We deploy the strategy and monitor real-time data to maximize reach.",
									icon: <Globe className="h-6 w-6" />
								}
							].map((item, i) => (
								<div key={i} className="group relative pl-12 py-16 border-b border-white/10 last:border-b-0 hover:bg-white/5 transition-colors duration-300">
									<div className="absolute left-[-9px] top-20 w-4 h-4 rounded-full bg-zinc-800 border-2 border-zinc-600 group-hover:bg-red-500 group-hover:border-red-500 transition-colors duration-300"></div>
									<span className="font-mono text-sm text-red-500 font-bold tracking-widest uppercase mb-4 block">Phase {item.step}</span>
									<h3 className="font-syne text-4xl md:text-5xl font-bold uppercase text-white mb-4 group-hover:translate-x-2 transition-transform duration-300">
										{item.title}
									</h3>
									<p className="font-inter text-xl text-zinc-400 max-w-lg">
										{item.desc}
									</p>
								</div>
							))}
						</div>
					</div>
				</div>
			</section>

			{/* FAQ Section */}
			<section className="py-32 relative overflow-hidden">
				<div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay pointer-events-none"></div>
				<div className="container mx-auto px-4 md:px-10 relative z-10">
					<div className="max-w-4xl mx-auto">
						<h2 className="font-syne text-5xl md:text-7xl font-bold uppercase tracking-tighter text-center text-white mb-16">
							Common <span className="text-zinc-600">Questions</span>
						</h2>

						<Accordion type="single" collapsible className="w-full space-y-4">
							{[
								{
									q: "How fast will I see results?",
									a: "Engagement typically spikes within the first 30 days. Real, sustainable subscriber growth follows shortly after as the algorithm picks up the new signals."
								},
								{
									q: "Is this organic growth?",
									a: "100%. We don't use bots or engagement farms. We use strategy to get your content in front of real humans who actually care about your niche."
								},
								{
									q: "Do you make the videos?",
									a: "We focus on strategy, packaging (thumbnails/titles), and promotion. You create the content, we make sure the world sees it."
								},
								{
									q: "Is my channel safe?",
									a: "Completely. We strictly adhere to YouTube's TOS. Our methods are white-hat and focused on long-term channel health."
								}
							].map((faq, i) => (
								<AccordionItem
									key={i}
									value={`item-${i}`}
									className="border border-white/10 bg-white/5 px-6 rounded-2xl data-[state=open]:bg-white/10 transition-all duration-300"
								>
									<AccordionTrigger className="font-syne text-xl font-bold uppercase text-white hover:text-red-500 hover:no-underline py-6">
										{faq.q}
									</AccordionTrigger>
									<AccordionContent className="font-inter text-lg text-zinc-400 pb-6">
										{faq.a}
									</AccordionContent>
								</AccordionItem>
							))}
						</Accordion>
					</div>
				</div>
			</section>

			{/* Massive CTA */}
			<section className="relative py-40 flex items-center justify-center overflow-hidden bg-red-600">
				<div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-40 mix-blend-overlay"></div>
				<div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0)_0%,rgba(0,0,0,0.4)_100%)]"></div>

				<div className="container relative z-10 px-4 text-center">
					<motion.div
						initial={{ scale: 0.9, opacity: 0 }}
						whileInView={{ scale: 1, opacity: 1 }}
						transition={{ duration: 0.5 }}
						viewport={{ once: true }}
					>
						<h2 className="font-syne text-[15vw] font-bold uppercase leading-[0.8] tracking-tighter text-white mix-blend-overlay mb-16">
							Go Viral
						</h2>
						<a
							href="https://www.thehighraysyt.com"
							target="_blank"
							rel="noopener noreferrer"
							className="inline-block"
						>
							<MagneticButton className="h-24 px-12 bg-black text-white rounded-full font-syne text-2xl font-bold uppercase tracking-wide hover:scale-110 transition-transform duration-300 shadow-2xl shadow-black/50 border border-white/20">
								Start Now
							</MagneticButton>
						</a>
					</motion.div>
				</div>
			</section>
		</div>
		</>
	);
}
